import {getAges, getAutomaticAge} from './tree';

const legacyParameterNames = {
  scope: 'bereich',
  problem: 'problem',
  age: 'alter',
  material: 'material'
};

/** Fallback, falls Magento bei einer isolierten lokalen Vorschau keine URL-Keys liefert. */
export function slugify(label) {
  return String(label || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, '-und-')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getFallbackLabel(tree, selection, key) {
  if (key === 'scope') return tree?.[selection.scope]?.label || '';
  if (key === 'problem') return tree?.[selection.scope]?.problems?.[selection.problem]?.label || '';
  if (key === 'age') return tree?.[selection.scope]?.problems?.[selection.problem]?.ages?.[selection.age]?.label || '';
  return tree?.[selection.scope]?.problems?.[selection.problem]?.ages?.[selection.age]
    ?.materials?.[selection.material]?.label || '';
}

/** Magento ist die verbindliche Quelle; die Label-Transliteration ist nur Fallback. */
function getUrlKey(tree, permalink, selection, key) {
  const code = selection[key];
  return permalink?.url_keys?.[key]?.[code] || slugify(getFallbackLabel(tree, selection, key));
}

function getBasePaths(permalink) {
  const paths = [...(permalink?.base_paths || []), permalink?.base_path || 'produktfinder', 'produktfinder'];
  return [...new Set(paths.map((path) => String(path).replace(/^\/+|\/+$/g, '').toLowerCase()).filter(Boolean))];
}

/** Findet Store-Code/Unterverzeichnis vor dem eigentlichen Produktfinder-Pfad. */
function splitPath(permalink, pathname = window.location.pathname) {
  const segments = String(pathname).split('/').filter(Boolean);
  const basePaths = getBasePaths(permalink);
  const baseIndex = segments.findIndex((segment) => basePaths.includes(decodeURIComponent(segment).toLowerCase()));
  return {
    prefix: baseIndex >= 0 ? segments.slice(0, baseIndex) : [],
    selectionSegments: baseIndex >= 0 ? segments.slice(baseIndex + 1).map(decodeURIComponent) : []
  };
}

function findCode(permalink, dimension, urlKey) {
  const normalized = String(urlKey || '').toLowerCase();
  const entry = Object.entries(permalink?.url_keys?.[dimension] || {})
    .find(([, key]) => String(key).toLowerCase() === normalized);
  return entry ? String(entry[0]) : '';
}

function readLegacyCode(params, key) {
  const match = String(params.get(legacyParameterNames[key]) || '').match(/^-?\d+/);
  return match ? match[0] : '';
}

/**
 * Erzeugt einen pfadbasierten Permalink. Nur fremde Query-Parameter bleiben
 * erhalten; die bisherigen Produktfinder-GET-Parameter werden stets entfernt.
 */
export function buildPermalink(tree, permalink, selection, currentHref = window.location.href) {
  const url = new URL(currentHref, window.location.origin);
  const {prefix} = splitPath(permalink, url.pathname);
  const path = [...prefix, permalink?.base_path || 'produktfinder'];

  if (selection.scope) path.push(getUrlKey(tree, permalink, selection, 'scope'));
  if (selection.problem) path.push(getUrlKey(tree, permalink, selection, 'problem'));
  // „Trifft nicht zu“ ist keine sichtbare Frage und erhält daher kein Segment.
  if (selection.age && selection.age !== '0') path.push(getUrlKey(tree, permalink, selection, 'age'));
  if (selection.material) path.push(getUrlKey(tree, permalink, selection, 'material'));

  url.pathname = `/${path.filter(Boolean).join('/')}`;
  Object.values(legacyParameterNames).forEach((parameter) => url.searchParams.delete(parameter));
  return url.toString();
}

/** Hält das Canonical-Element auch nach clientseitigen History-Wechseln aktuell. */
export function syncCanonical(currentHref = window.location.href) {
  const canonicalUrl = new URL(currentHref, window.location.origin);
  canonicalUrl.search = '';
  canonicalUrl.hash = '';
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', canonicalUrl.toString());
}

/** Prüft einen Pfad strikt entlang des aktuell verfügbaren Entscheidungsbaums. */
function readPathState(tree, permalink, pathname) {
  const segments = splitPath(permalink, pathname).selectionSegments;
  const selection = {scope: '', problem: '', age: '', material: ''};
  const scope = findCode(permalink, 'scope', segments[0]);
  if (!scope || !tree?.[scope]) return {selection, step: 'area'};

  selection.scope = scope;
  const problem = findCode(permalink, 'problem', segments[1]);
  if (!problem || !tree[scope]?.problems?.[problem]) return {selection, step: 'problem'};

  selection.problem = problem;
  const automaticAge = getAutomaticAge(getAges(tree, scope, problem));
  if (automaticAge !== null) {
    selection.age = automaticAge;
    const material = findCode(permalink, 'material', segments[2]);
    if (!material || !tree[scope]?.problems?.[problem]?.ages?.[automaticAge]?.materials?.[material]) {
      return {selection, step: 'material'};
    }
    selection.material = material;
    return {selection, step: 'results'};
  }

  const age = findCode(permalink, 'age', segments[2]);
  if (!age || !tree[scope]?.problems?.[problem]?.ages?.[age]) return {selection, step: 'age'};
  selection.age = age;
  const material = findCode(permalink, 'material', segments[3]);
  if (!material || !tree[scope]?.problems?.[problem]?.ages?.[age]?.materials?.[material]) {
    return {selection, step: 'material'};
  }
  selection.material = material;
  return {selection, step: 'results'};
}

/** Liest alte Parameterlinks einmalig ein; seedHistory ersetzt sie sofort kanonisch. */
function readLegacyState(tree, search) {
  const params = new URLSearchParams(search);
  const selection = {scope: '', problem: '', age: '', material: ''};
  const scope = readLegacyCode(params, 'scope');
  if (!scope || !tree?.[scope]) return {selection, step: 'area'};
  selection.scope = scope;
  const problem = readLegacyCode(params, 'problem');
  if (!problem || !tree[scope]?.problems?.[problem]) return {selection, step: 'problem'};
  selection.problem = problem;
  const automaticAge = getAutomaticAge(getAges(tree, scope, problem));
  const age = automaticAge !== null ? automaticAge : readLegacyCode(params, 'age');
  if (age === '' || !tree[scope]?.problems?.[problem]?.ages?.[age]) {
    return {selection, step: automaticAge !== null ? 'material' : 'age'};
  }
  selection.age = age;
  const material = readLegacyCode(params, 'material');
  if (!material || !tree[scope]?.problems?.[problem]?.ages?.[age]?.materials?.[material]) {
    return {selection, step: 'material'};
  }
  selection.material = material;
  return {selection, step: 'results'};
}

export function readStateFromUrl(tree, permalink, location = window.location) {
  const hasLegacyParameters = Object.values(legacyParameterNames)
    .some((parameter) => new URLSearchParams(location.search).has(parameter));
  return hasLegacyParameters
    ? readLegacyState(tree, location.search)
    : readPathState(tree, permalink, location.pathname);
}

/** Baut auch bei direkt geöffneten Ergebnislinks alle Browser-Zwischenschritte. */
export function seedHistory(tree, permalink, target) {
  const empty = {scope: '', problem: '', age: '', material: ''};
  const trail = [{selection: empty, step: 'area'}];
  if (target.selection.scope) trail.push({selection: {...empty, scope: target.selection.scope}, step: 'problem'});
  if (target.selection.problem) {
    const next = {...empty, scope: target.selection.scope, problem: target.selection.problem};
    const automaticAge = getAutomaticAge(getAges(tree, next.scope, next.problem));
    if (automaticAge !== null) next.age = automaticAge;
    trail.push({selection: next, step: automaticAge !== null ? 'material' : 'age'});
  }
  if (target.selection.age && target.selection.age !== '0') {
    trail.push({selection: {...target.selection, material: ''}, step: 'material'});
  }
  if (target.selection.material) trail.push({selection: target.selection, step: 'results'});

  const [first, ...rest] = trail;
  window.history.replaceState({kotoriProductfinder: true, ...first}, '', buildPermalink(tree, permalink, first.selection));
  rest.forEach((entry) => {
    window.history.pushState({kotoriProductfinder: true, ...entry}, '', buildPermalink(tree, permalink, entry.selection));
  });
  syncCanonical();
  return trail[trail.length - 1];
}

export function pushHistory(tree, permalink, selection, step) {
  window.history.pushState(
    {kotoriProductfinder: true, selection, step},
    '',
    buildPermalink(tree, permalink, selection)
  );
  syncCanonical();
}
